import { nextTick, onMounted, onUnmounted, watch } from 'vue'

/**
 * Reveals elements (adds `.revealed`) as they scroll into view.
 *
 * Unlike a plain onMounted observer, this re-scans whenever `source` changes, so
 * elements rendered later — cards that arrive with async data — are picked up too.
 * Without the re-scan they stay stuck at their `opacity: 0` start state forever.
 *
 * @param {string} selector  CSS selector for the elements to reveal
 * @param {Function} [source] optional reactive getter to re-scan on (e.g. () => items.value)
 */
export function useScrollReveal(selector, source) {
  let observer

  const revealAll = () => {
    document.querySelectorAll(selector).forEach((el) => el.classList.add('revealed'))
  }

  const scan = () => {
    if (!observer) return
    document.querySelectorAll(selector).forEach((el) => {
      if (!el.classList.contains('revealed')) observer.observe(el)
    })
  }

  onMounted(() => {
    if (typeof IntersectionObserver === 'undefined') {
      revealAll()
      return
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('revealed')
          observer.unobserve(entry.target)
        })
      },
      // threshold 0 so tall cards reveal as soon as any part enters the viewport
      { threshold: 0, rootMargin: '0px 0px -60px 0px' }
    )

    nextTick(scan)
  })

  if (source) {
    watch(source, () => nextTick(scan), { deep: false })
  }

  onUnmounted(() => {
    if (observer) observer.disconnect()
  })

  return { scan, revealAll }
}
